import { useMemo } from 'react';
import {
  type GetHostAvailabilityRequestsQuery,
  SportsEnum,
  useGetHostAvailabilityQuery,
  useGetHostAvailabilityRequestsQuery,
} from 'types/generated/client';
import { getVerificationLevel } from 'types/verification';
import getFirstNameLastInitial from 'utils/shared/string/getFirstNameLastInitial';
import { formatVenueDateParts } from 'utils/shared/time/formatVenueTimeRange';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useViewer } from 'hooks/useViewer';
import type { LatLng } from 'screens/Find/geo';
import type { FindPlayer } from 'screens/Find/types';
import {
  buildVenueDetails,
  formatDay,
  formatTime,
  mapAvailabilityToPlayer,
} from 'screens/Find/useFindPlayers';
import type { AvailableOnSlot, HostPlayRequest } from './types';

type HostRequestRow = GetHostAvailabilityRequestsQuery['playRequests'][number];

/** A picked hour ("Sep 16", "Wed", "6 – 7 PM") or the weekly window ("Wed", "6 pm - 8 pm"). */
const buildAvailableOn = (
  row: HostRequestRow,
  timezone?: string,
): AvailableOnSlot[] | undefined => {
  const slots = row.slots.map((slot) => {
    if (slot.requestedStartAt && slot.requestedEndAt) {
      return {
        id: slot.id,
        parts: formatVenueDateParts(slot.requestedStartAt, slot.requestedEndAt, timezone),
      };
    }
    const { dayOfWeek, startTime, endTime } = slot.availabilitySlot;
    return {
      id: slot.id,
      parts: [formatDay(dayOfWeek), `${formatTime(startTime)} - ${formatTime(endTime)}`],
    };
  });
  return slots.length > 0 ? slots : undefined;
};

/** The requester's first chosen court, as the shared venue detail card. */
const buildLocation = (row: HostRequestRow) => {
  const venue = row.venues[0]?.venue;
  return venue ? buildVenueDetails(venue) : undefined;
};

/** Maps one pending play request to the host-review `HostPlayRequest` shape.
 *  Mirrors `buildHostPlayer`: skill comes from the requester's profile level for
 *  the availability's sport (no fabricated default when unknown). */
const mapRequest = (
  row: HostRequestRow,
  player: Pick<FindPlayer, 'sport' | 'lookingForSummary'>,
  timezone?: string,
): HostPlayRequest => {
  const { sport } = player;
  const profile = row.requesterProfile;
  const displayName = profile?.preferredName || profile?.fullName || 'Player';
  const profileSkillLevel =
    sport === SportsEnum.Tennis
      ? profile?.tennisSkillLevel?.id
      : sport === SportsEnum.Pickleball
      ? profile?.pickleballSkillLevel?.id
      : undefined;

  return {
    id: row.id,
    variant: 'AVAILABILITY',
    message: row.requestMessage,
    activity: { sport, title: player.lookingForSummary },
    availableOn: buildAvailableOn(row, timezone),
    location: buildLocation(row),
    neighborhoods: row.neighborhoods.map((picked) => picked.neighborhood.name),
    player: {
      name: getFirstNameLastInitial(displayName),
      username: profile?.username ?? undefined,
      imageUrl: getProfileImageUrlOrPlaceholder({ path: profile?.profileImagePath }),
      verificationLevel: getVerificationLevel({
        identityVerifications: profile?.identityVerifications,
      }),
      skillLevel: profileSkillLevel ?? undefined,
    },
  };
};

/**
 * A host's player availability plus its pending play requests, mapped to the
 * presentational `FindPlayer` / `HostPlayRequest` shapes the detail modal renders.
 * Skips until both an availability id and the viewer are known; `cache-and-network`
 * so a request just accepted/declined elsewhere is reflected.
 */
export function useHostAvailabilityDetail(
  availabilityId: string | null,
  /** Drives the "5mi" on each preferred court; distances are omitted when null. */
  viewerCenter: LatLng | null = null,
) {
  const { userId } = useViewer();
  const skip = !availabilityId || !userId;

  const { data: availabilityData, loading: availabilityLoading } = useGetHostAvailabilityQuery({
    variables: { availabilityId: availabilityId ?? '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const { data: requestsData, loading: requestsLoading } = useGetHostAvailabilityRequestsQuery({
    variables: { availabilityId: availabilityId ?? '', userId: userId ?? '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const player = useMemo<FindPlayer | null>(() => {
    const availability = availabilityData?.playerAvailabilities?.[0];
    return availability ? mapAvailabilityToPlayer(availability, viewerCenter, null) : null;
  }, [availabilityData, viewerCenter]);

  const requests = useMemo<HostPlayRequest[]>(() => {
    if (!player) return [];
    return (requestsData?.playRequests ?? []).map((row) =>
      mapRequest(row, player, player.timezone),
    );
  }, [requestsData, player]);

  return { player, requests, loading: availabilityLoading || requestsLoading };
}
